import React, { useMemo, useRef } from 'react'
import { Link } from 'react-router-dom'
import { ChartBar } from '../../../shared/chart/chart'
import { FleetGrid, useGridList } from '../../../shared/sso-workspace/fleet-grid'
import { IconAnalytic } from '../../../shared/icon/icon'
import { TableAgClient } from '../../../shared/table-builder/table-ag-client'
import { ColumnSelector, ColumnText } from '../../../shared/table-builder/table-columns'
import { ChartBox } from './chart-box'
import { COLOR_GREYED, COLOR_REFUSALS, SOURCE_TEXT, TRUNCATED_TEXT, UNKNOWN, bucketLabel, countText, formatCount, numericComparator } from './launch-format'
import { beforeCutoff, formatCutoff, laterCutoff } from './lms-insight-query'

// The refusals of the Launches tab: where a launch was refused, by which code,
// how often, and how many launches never got in.

// The LMS Activity feed, showing the launches the platform refused.
export const REFUSED_ACTIVITY = '/org/lms/activity?event_type=lms.launch.refused'

const plural = (count, one, many) => `${formatCount(count)} ${count === 1 ? one : many}`

// The attempts of each code, the most first (a code seen in two sources is
// one bar).
export const attemptsByCode = rows => {
  const totals = new Map()
  rows.forEach(row => totals.set(row.code, (totals.get(row.code) || 0) + row.attempts))
  return [...totals.entries()].sort((a, b) => b[1] - a[1])
}

const nameOrDash = field => row => row[field] || UNKNOWN
const sourceText = row => SOURCE_TEXT[row.source] || row.source

// buckets and the cutoffs come from the series. The refusals over time are of
// two kinds, kept for different times: the audit trail's (mint and LTI,
// eventsRetainedFrom) and the counted ones (rate limited, unknown intent,
// countsRetainedFrom, 90 days). A bucket that began before either cutoff is
// missing some of its refusals, so every bucket that begins before the later
// of the two is drawn grey. Each cutoff has a one-line note of its own, shown
// while the range reaches back before it.
export const LaunchRefusals = ({ refusals, series, tz }) => {
  const rows = refusals.rows
  const tableRef = useRef()
  const gridList = useGridList(rows)
  const emptyState = useMemo(() => ({ Icon: IconAnalytic, title: 'No refusals in this range.' }), [])

  const buckets = series.buckets
  const byCode = useMemo(() => attemptsByCode(rows), [rows])
  const codeLabels = useMemo(() => byCode.map(([code]) => code), [byCode])
  const codeDataset = useMemo(
    () => ({ label: 'Attempts', data: byCode.map(([, attempts]) => attempts), backgroundColor: COLOR_REFUSALS }),
    [byCode]
  )

  const bucketLabels = useMemo(() => buckets.map(bucket => bucketLabel(bucket.bucketStart)), [buckets])
  const keptFrom = laterCutoff(series.eventsRetainedFrom, series.countsRetainedFrom)
  const colors = useMemo(
    () => buckets.map(bucket => (beforeCutoff(bucket.bucketStart, keptFrom) ? COLOR_GREYED : COLOR_REFUSALS)),
    [buckets, keptFrom]
  )
  const bucketDataset = useMemo(
    () => ({ label: 'Refusals', data: buckets.map(bucket => bucket.refusals), backgroundColor: colors }),
    [buckets, colors]
  )
  const eventsGreyed = buckets.some(bucket => beforeCutoff(bucket.bucketStart, series.eventsRetainedFrom))
  const countsGreyed = buckets.some(bucket => beforeCutoff(bucket.bucketStart, series.countsRetainedFrom))

  return (
    <section className="mt-4" aria-labelledby="lms-refusals-title" data-testid="lms-launch-refusals">
      <h3 className="h6" id="lms-refusals-title">Refusals</h3>
      <p className="mb-2" data-testid="refusal-totals">
        {plural(refusals.totals.attempts, 'refused attempt', 'refused attempts')}, {plural(refusals.totals.refusedLaunches, 'refused launch', 'refused launches')} that never signed in
      </p>

      {rows.length > 0
        ? <div className="row g-3 mb-2">
            <div className="col-lg-7">
              <h4 className="fs-6 text-body-secondary">Refusals over time</h4>
              <ChartBox label="Refusals over time" height={240}>
                <ChartBar labels={bucketLabels} data={bucketDataset} displayGrid displayTicks />
              </ChartBox>
            </div>
            <div className="col-lg-5">
              <h4 className="fs-6 text-body-secondary">Refusals by code</h4>
              <ChartBox label="Refusals by code" height={240}>
                <ChartBar labels={codeLabels} data={codeDataset} displayGrid displayTicks />
              </ChartBox>
            </div>
          </div>
        : <p className="text-body-secondary">No refusals in this range.</p>}

      {eventsGreyed &&
        <p className="small text-body-secondary mb-1" data-testid="retention-events">
          Refusals before {formatCutoff(series.eventsRetainedFrom, tz)} are no longer kept.
        </p>}
      {countsGreyed &&
        <p className="small text-body-secondary mb-1" data-testid="retention-counts">
          Counted refusals before {formatCutoff(series.countsRetainedFrom, tz)} are no longer kept.
        </p>}

      <h4 className="fs-6 text-body-secondary mt-3">Refusals by connection, course, source and code</h4>
      <FleetGrid compact tableRef={tableRef} testId="lms-launch-refusal-rows">
        <TableAgClient ref={tableRef} apiList={gridList} queryKey="refusals" sortCol="attempts" sortDesc emptyState={emptyState}>
          <ColumnSelector field="connectionName" header="LMS connection" selector={nameOrDash('connectionName')} sortable minWidth={170} flex={1} />
          <ColumnSelector field="courseName" header="Course" selector={nameOrDash('courseName')} sortable minWidth={170} flex={1} />
          <ColumnSelector field="source" header="Source" selector={sourceText} sortable />
          <ColumnText field="code" header="Code" sortable minWidth={200} flex={1} />
          <ColumnSelector field="attempts" header="Attempts" selector={countText('attempts')} sortable />
          <ColumnSelector field="refusedLaunches" header="Refused launches" selector={countText('refusedLaunches')} comparator={numericComparator} sortable />
        </TableAgClient>
      </FleetGrid>
      {refusals.truncated && <p className="small text-body-secondary mt-2 mb-0" data-testid="truncated-refusals">{TRUNCATED_TEXT}</p>}

      {Array.isArray(refusals.notes) && refusals.notes.length > 0 &&
        <ul className="small text-body-secondary mt-3 mb-2 ps-3" data-testid="refusal-notes">
          {refusals.notes.map(note => <li key={note}>{note}</li>)}
        </ul>}

      <p className="mb-0">
        <Link to={REFUSED_ACTIVITY}>Open refused launches in Activity</Link>
      </p>
    </section>
  )
}
